import { useCallback } from 'react';
import {
  ProjectBriefsService,
  type BriefResponseDto,
  type BriefSectionResponseDto,
  type ReconcileTemplateResponseDto,
} from '@inference.engine/workspace-api-client/web';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { briefByIdQueryOptions } from '@/entities/brief';
import { workspaceHttp } from '@/lib/api/http-client';
import { notificationService } from '@/lib/services/notification.service';

import { describeReconcileOutcome } from '../../lib/describeReconcileOutcome';

export interface UseReconcileBriefTemplateParams {
  projectId: string;
  briefId: string;
  /** The sections as they stand now, compared against the refetched brief to see what moved. */
  sections: readonly BriefSectionResponseDto[];
  /** Hands the refetched sections to the form so a newly added field actually appears. */
  onSectionsAdopted: (sections: readonly BriefSectionResponseDto[]) => void;
}

export interface UseReconcileBriefTemplateApi {
  reconcile: () => void;
  isReconciling: boolean;
}

/**
 * The "Check for template updates" action.
 *
 * The brief is refetched only when the run reports it wrote something, so a refusal costs one
 * request rather than two and cannot disturb the editor. What was added or retired is then read
 * off the sections either side of the run, because the endpoint does not count those. An answer
 * the run EMPTIED is the exception: narrowing a field adds and retires nothing, so the two
 * snapshots are identical and only the endpoint's `clearedSections` records the deletion.
 */
export function useReconcileBriefTemplate({
  projectId,
  briefId,
  sections,
  onSectionsAdopted,
}: UseReconcileBriefTemplateParams): UseReconcileBriefTemplateApi {
  const { t } = useTranslation('projects');
  const queryClient = useQueryClient();

  const mutation = useMutation<
    { result: ReconcileTemplateResponseDto; fresh: BriefResponseDto | null },
    Error
  >({
    mutationFn: async () => {
      const response = await ProjectBriefsService.reconcileTemplate({
        client: workspaceHttp,
        path: { platformProjectId: projectId, briefId },
      });

      const result = response.data ?? { reconciled: false };
      if (!result.reconciled) return { result, fresh: null };

      // Bypasses any staleTime so the answer reflects the write that just happened, and lands
      // in the same cache entry the page is already rendering from.
      const fresh = await queryClient.fetchQuery({
        ...briefByIdQueryOptions({ projectId, briefId }),
        staleTime: 0,
      });

      return { result, fresh };
    },
    onSuccess: ({ result, fresh }) => {
      if (fresh) onSectionsAdopted(fresh.sections);

      const outcome = describeReconcileOutcome(result, sections, fresh?.sections ?? sections);

      const clearedMessage = (cleared: readonly string[]) =>
        t('projectBriefDetail.reconcileTemplate.clearedAnswers', {
          count: cleared.length,
          fields: cleared.join(', '),
        });

      switch (outcome.kind) {
        case 'changed': {
          const structure =
            outcome.retired === 0
              ? t('projectBriefDetail.reconcileTemplate.addedFields', { count: outcome.added })
              : outcome.added === 0
                ? t('projectBriefDetail.reconcileTemplate.retiredFields', {
                    count: outcome.retired,
                  })
                : t('projectBriefDetail.reconcileTemplate.addedAndRetiredFields', {
                    added: outcome.added,
                    retired: outcome.retired,
                  });

          // A deleted answer outranks an added field: one is work offered, the other is work
          // taken away, so it leads and the rest of the run rides along as the detail.
          if (outcome.cleared.length > 0) {
            notificationService.showWarning(clearedMessage(outcome.cleared), {
              description: structure,
            });
            return;
          }

          notificationService.showSuccess(structure);
          return;
        }
        case 'cleared':
          notificationService.showWarning(clearedMessage(outcome.cleared));
          return;
        case 'nothing-to-do':
          notificationService.showInfo(t('projectBriefDetail.reconcileTemplate.upToDate'));
          return;
        case 'no-template':
          notificationService.showInfo(t('projectBriefDetail.reconcileTemplate.noTemplate'));
          return;
        case 'template-unavailable':
          notificationService.showWarning(
            t('projectBriefDetail.reconcileTemplate.templateUnavailable'),
          );
          return;
      }
    },
    onError: (error) => {
      notificationService.showError(t('projectBriefDetail.reconcileTemplate.failed'), {
        description: error.message,
      });
    },
  });

  const { mutate, isPending } = mutation;
  const reconcile = useCallback(() => {
    if (isPending) return;
    mutate();
  }, [mutate, isPending]);

  return { reconcile, isReconciling: isPending };
}
